import { Box, MenuItem, Stack, Typography } from "@mui/material";
import { Views } from "../../../utils/types";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import InsightsIcon from "@mui/icons-material/Insights";
import PhoneLinkIcon from "@mui/icons-material/Phonelink";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { Link, useLocation } from "react-router";
import PeopleIcon from "@mui/icons-material/People";
import { UserPermissionGate } from "../../user-permission-gate";
import { useCapabilities } from "@hooks/use-capabilities";
import { colors, skyTint, whiteTint } from "../../../utils/colors";

type LinkConfig = {
    to: string;
    label: string;
    icon: React.ReactNode;
    /** Capability required for this link; omit for links visible to anyone with `read`. */
    cap?: string;
};

const links: LinkConfig[] = [
    { to: Views.DASHBOARD, label: "Dashboard", icon: <DashboardIcon fontSize="small" />, cap: "view_dashboard" },
    { to: Views.ASSET_TABLE, label: "Assets", icon: <PhoneLinkIcon fontSize="small" />, cap: "read_asset" },
    { to: Views.TICKET_TABLE, label: "Tickets", icon: <LibraryBooksIcon fontSize="small" />, cap: "read_ticket" },
    { to: Views.USER_TABLE, label: "Users", icon: <PeopleIcon fontSize="small" />, cap: "list_users" },
    { to: Views.REPORTING, label: "Report Insights", icon: <InsightsIcon fontSize="small" />, cap: "read_reports" },
    { to: Views.PROFILE, label: "My Profile", icon: <AccountBoxIcon fontSize="small" /> },
];

export const SidebarMenu = ({ onClose }: { onClose?: () => void }) => {
    const { can } = useCapabilities();
    const { pathname } = useLocation();
    if (!can("read")) return null;

    return (
        <Box sx={{ color: colors.surface, px: 1.25, py: 0.5 }}>
            {links.map((link) => {
                const isActive =
                    pathname === link.to || pathname.startsWith(`${link.to}/`);

                return (
                    <UserPermissionGate caps={link.cap} key={link.to}>
                        <MenuItem
                            component={Link}
                            to={link.to}
                            key={link.to}
                            onClick={onClose}
                            sx={{
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: isActive ? colors.lime : "transparent",
                                mb: 0.5,
                                py: 1,
                                pl: 1.25,
                                color: isActive
                                    ? colors.surface
                                    : whiteTint(0.72),
                                fontWeight: isActive ? 700 : 500,
                                bgcolor: isActive
                                    ? skyTint(0.12)
                                    : "transparent",
                                transition:
                                    "background-color .2s ease, color .2s ease, border-color .2s ease, transform .2s ease",
                                "&:hover": {
                                    bgcolor: isActive
                                        ? skyTint(0.16)
                                        : whiteTint(0.06),
                                    color: colors.surface,
                                    borderColor: colors.lime,
                                    transform: "translateX(2px)",
                                },
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                alignContent="flex-start"
                                spacing={1.25}
                            >
                                <Box
                                    className="menu-item-icon"
                                    sx={{
                                        display: "flex",
                                        color: isActive ? colors.sky : "inherit",
                                        pr: 1,
                                        mr: 0.25,
                                        borderRight: "1px solid",
                                        borderRightColor: isActive ? colors.lime : "transparent",
                                        transition: "border-right-color .2s ease",
                                        ".MuiMenuItem-root:hover &": {
                                            borderRightColor: colors.lime,
                                        },
                                    }}
                                >
                                    {link.icon}
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "inherit", fontSize: ".88rem" }}
                                >
                                    {link.label}
                                </Typography>
                            </Stack>
                        </MenuItem>
                    </UserPermissionGate>
                );
            })}
        </Box>
    );
};
